import React from 'react';
import StatusBadge from './StatusBadge';
import { formatPhone, getWhatsAppLink } from '../utils/phoneUtils';

const OrderDetailModal = ({ order, onClose, onStatusChange, formatDate, formatCurrency }) => {
  const handleStatusChange = (newStatus) => {
    onStatusChange(order.id, newStatus);
  };


  return (
    <div className="fixed inset-0 bg-cocoa-950/40 backdrop-blur-[2px] flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full my-8 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-cream-200 px-6 py-4 rounded-t-2xl z-10">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-ink">Detalhes do Pedido #{order.id}</h2>
              <p className="text-sm text-ink-muted mt-1">
                Criado em {formatDate(order.created_at)}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-ink-muted hover:text-ink-soft transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Status e Ações Rápidas */}
          <div className="rounded-xl border border-cream-200 bg-cream-50 p-6 mb-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <p className="text-sm text-ink-soft mb-2">Status Atual</p>
                <StatusBadge status={order.status} size="large" />
              </div>
              
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => handleStatusChange('paid')}
                  disabled={order.status && order.status.toUpperCase() === 'PAID'}
                  className="rounded-full bg-cocoa-800 px-4 py-2 text-sm font-medium text-cream-50 transition-colors hover:bg-cocoa-900 disabled:pointer-events-none disabled:opacity-40"
                >
                  Marcar como Pago
                </button>
                <button
                  onClick={() => handleStatusChange('processing')}
                  disabled={order.status && order.status.toUpperCase() === 'PROCESSING'}
                  className="rounded-full border border-cocoa-300 bg-white px-4 py-2 text-sm font-medium text-cocoa-800 transition-colors hover:bg-cocoa-50 disabled:pointer-events-none disabled:opacity-40"
                >
                  Em produção
                </button>
                <button
                  onClick={() => handleStatusChange('ready')}
                  disabled={order.status && order.status.toUpperCase() === 'READY'}
                  className="rounded-full border border-cocoa-300 bg-white px-4 py-2 text-sm font-medium text-cocoa-800 transition-colors hover:bg-cocoa-50 disabled:pointer-events-none disabled:opacity-40"
                >
                  Marcar Pronto
                </button>
                <button
                  onClick={() => handleStatusChange('delivered')}
                  disabled={order.status && order.status.toUpperCase() === 'DELIVERED'}
                  className="rounded-full border border-cocoa-300 bg-white px-4 py-2 text-sm font-medium text-cocoa-800 transition-colors hover:bg-cocoa-50 disabled:pointer-events-none disabled:opacity-40"
                >
                  Marcar Entregue
                </button>
                <button
                  onClick={() => handleStatusChange('cancelled')}
                  disabled={order.status && order.status.toUpperCase() === 'CANCELLED'}
                  className="rounded-full bg-clay-50 px-4 py-2 text-sm font-medium text-clay-700 transition-colors hover:bg-clay-100 disabled:pointer-events-none disabled:opacity-40"
                >
                  Cancelar pedido
                </button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Informações do Cliente */}
            <div className="bg-white border border-cream-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-ink mb-4 flex items-center gap-2">
                <svg className="w-5 h-5 text-cocoa-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                Informações do Cliente
              </h3>
              <div className="space-y-3">
                {order.customer_name && (
                  <div>
                    <p className="text-sm text-ink-soft">Nome</p>
                    <p className="text-base font-medium text-ink">{order.customer_name}</p>
                  </div>
                )}
                {order.customer_email && (
                  <div>
                    <p className="text-sm text-ink-soft">Email</p>
                    <p className="text-base font-medium text-ink">{order.customer_email}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-ink-soft">Telefone</p>
                  <div className="flex items-center gap-2">
                    <p className="text-base font-medium text-ink">{formatPhone(order.phone_number || order.customer_phone) || 'N/A'}</p>
                    {(order.phone_number || order.customer_phone) && (
                      <a
                        href={getWhatsAppLink(order.phone_number || order.customer_phone)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sage-600 hover:text-sage-700 transition-colors"
                        title="Abrir WhatsApp"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                        </svg>
                      </a>
                    )}
                  </div>
                </div>
                {order.payment_method && (
                  <div>
                    <p className="text-sm text-ink-soft">Método de Pagamento</p>
                    <p className="text-base font-medium text-ink">{order.payment_method}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Informações de Entrega */}
            <div className="bg-white border border-cream-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-ink mb-4 flex items-center gap-2">
                {(order.shipping_type || order.shippingType)?.toUpperCase() === 'PICKUP' ? (
                  <>
                    <svg className="w-5 h-5 text-cocoa-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    Retirada no Local
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 text-cocoa-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Endereço de Entrega
                  </>
                )}
              </h3>
              
              {(order.shipping_type || order.shippingType)?.toUpperCase() === 'PICKUP' ? (
                <div className="rounded-xl bg-cream-50 p-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-cocoa-100 flex items-center justify-center">
                      <svg className="w-6 h-6 text-cocoa-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-base font-medium text-ink">Cliente irá retirar no local</p>
                      <p className="text-sm text-cocoa-700">Sem necessidade de entrega</p>
                    </div>
                  </div>
                </div>
              ) : (() => {
                // Suporta tanto campos flat (order.shipping_city) quanto objeto aninhado (order.shipping_address.city)
                const addr = (order.shipping_address && typeof order.shipping_address === 'object') ? order.shipping_address : null;
                const recipientName = order.recipient_name || (addr && addr.recipient_name) || '';
                const street = (addr && addr.street) || (typeof order.shipping_address === 'string' ? order.shipping_address : '');
                const number = order.shipping_number || (addr && addr.number) || '';
                const complement = order.shipping_complement || (addr && addr.complement) || '';
                const neighborhood = order.shipping_neighborhood || (addr && addr.neighborhood) || '';
                const zipCode = order.shipping_zip_code || (addr && addr.zip_code) || '';
                const city = order.shipping_city || (addr && addr.city) || '';
                const state = order.shipping_state || (addr && addr.state) || '';

                return (
                  <div className="space-y-3">
                    {recipientName && (
                      <div>
                        <p className="text-sm text-ink-soft">Destinatário</p>
                        <p className="text-base font-medium text-ink">{recipientName}</p>
                      </div>
                    )}
                    {street && (
                      <div>
                        <p className="text-sm text-ink-soft">Endereço</p>
                        <p className="text-base font-medium text-ink">{street}</p>
                      </div>
                    )}
                    {(number || complement) && (
                      <div className="grid grid-cols-2 gap-4">
                        {number && (
                          <div>
                            <p className="text-sm text-ink-soft">Número</p>
                            <p className="text-base font-medium text-ink">{number}</p>
                          </div>
                        )}
                        {complement && (
                          <div>
                            <p className="text-sm text-ink-soft">Complemento</p>
                            <p className="text-base font-medium text-ink">{complement}</p>
                          </div>
                        )}
                      </div>
                    )}
                    {neighborhood && (
                      <div>
                        <p className="text-sm text-ink-soft">Bairro</p>
                        <p className="text-base font-medium text-ink">{neighborhood}</p>
                      </div>
                    )}
                    {zipCode && (
                      <div>
                        <p className="text-sm text-ink-soft">CEP</p>
                        <p className="text-base font-medium text-ink">{zipCode}</p>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-4">
                      {city && (
                        <div>
                          <p className="text-sm text-ink-soft">Cidade</p>
                          <p className="text-base font-medium text-ink">{city}</p>
                        </div>
                      )}
                      {state && (
                        <div>
                          <p className="text-sm text-ink-soft">Estado</p>
                          <p className="text-base font-medium text-ink">{state}</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Observações */}
          {(order.observation || order.notes) && (
            <div className="rounded-xl bg-honey-50 border border-honey-200 p-4 mb-6">
              <h3 className="text-sm font-semibold text-honey-700 mb-2 flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                </svg>
                Observações do Cliente
              </h3>
              <p className="text-sm text-ink-soft">{order.observation || order.notes}</p>
            </div>
          )}

          {/* Items do Pedido */}
          <div className="bg-white border border-cream-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-ink mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-cocoa-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
              Items do Pedido ({(order.order_items || order.items)?.length || 0})
            </h3>
            
            <div className="space-y-4">
              {(order.order_items || order.items) && (order.order_items || order.items).length > 0 ? (
                (order.order_items || order.items).map((item, index) => (
                  <div 
                    key={item.id} 
                    className="border border-cream-200 rounded-lg p-4 hover:shadow-md transition-shadow bg-cream-50"
                  >
                    <div className="flex gap-4">
                      {/* Imagem do Item */}
                      <div className="flex-shrink-0">
                        <div className="w-32 h-32 bg-cream-100 rounded-lg overflow-hidden border-2 border-cream-200 shadow-sm">
                          {(item.image || item.image_url) ? (
                            <img 
                              src={item.image || item.image_url} 
                              alt={item.product_name || item.product_type}
                              className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"%3E%3Crect fill="%23f3f4f6" width="100" height="100"/%3E%3Ctext fill="%239ca3af" font-family="sans-serif" font-size="14" text-anchor="middle" x="50" y="50" dy=".3em"%3ESem Imagem%3C/text%3E%3C/svg%3E';
                              }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-ink-muted">
                              <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Detalhes do Item */}
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="text-base font-semibold text-ink">{item.product_name || item.product_type || 'Produto'}</h4>
                            {item.product_type && <p className="text-sm text-ink-soft mt-1">{item.product_type}</p>}
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-cocoa-700">
                              {formatCurrency(item.unit_price)}
                            </p>
                            <p className="text-xs text-ink-muted">por unidade</p>
                          </div>
                        </div>

                        <div className="mt-3 grid grid-cols-2 gap-3">
                          <div className="bg-white rounded-lg px-3 py-2 border border-cream-200">
                            <p className="text-xs text-ink-soft">Quantidade</p>
                            <p className="text-base font-semibold text-ink">
                              {item.quantity} {item.quantity === 1 ? 'unidade' : 'unidades'}
                            </p>
                          </div>
                          <div className="rounded-xl border border-cream-200 bg-cream-50 px-3 py-2">
                            <p className="text-xs text-cocoa-700">Subtotal</p>
                            <p className="text-base font-semibold text-ink">
                              {formatCurrency(item.unit_price * item.quantity)}
                            </p>
                          </div>
                        </div>

                        {/* Visualização ampliada da imagem ao clicar */}
                        {(item.image || item.image_url) && (
                          <button
                            onClick={() => window.open(item.image || item.image_url, '_blank')}
                            className="mt-2 text-sm text-cocoa-700 hover:text-cocoa-900 font-medium flex items-center gap-1"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                            </svg>
                            Visualizar imagem em tamanho real
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-ink-muted text-center py-4">Nenhum item no pedido</p>
              )}
            </div>
          </div>

          {/* Resumo Financeiro */}
          <div className="mt-6 rounded-xl border border-cream-200 bg-cream-50 p-6">
            <h3 className="text-lg font-semibold text-ink mb-4">Resumo Financeiro</h3>
            <div className="space-y-2">
              <div className="flex justify-between text-ink-soft">
                <span>Subtotal dos Items</span>
                <span className="font-medium">
                  {formatCurrency(
                    (order.order_items || order.items)?.reduce((sum, item) => {
                      const unitPrice = parseFloat(item.unit_price) || 0;
                      const quantity = parseInt(item.quantity) || 0;
                      const subtotal = parseFloat(item.subtotal) || (unitPrice * quantity);
                      return sum + subtotal;
                    }, 0) || 0
                  )}
                </span>
              </div>
              {order.shipping_cost && (
                <div className="flex justify-between text-ink-soft">
                  <span>Frete</span>
                  <span className="font-medium">{formatCurrency(order.shipping_cost)}</span>
                </div>
              )}
              <div className="border-t border-cream-300 pt-2 mt-2">
                <div className="flex justify-between text-lg font-bold text-ink">
                  <span>Total do Pedido</span>
                  <span className="text-cocoa-700">{formatCurrency(order.total_price || order.total_amount || 0)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Token de Rastreamento */}
          {(order.token || order.tracking_token) && (
            <div className="mt-6 rounded-xl border border-cream-200 bg-cream-50 p-4">
              <h3 className="text-sm font-semibold text-ink mb-2 flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
                Token de Rastreamento
              </h3>
              <p className="text-sm font-mono text-ink bg-cream-200 px-3 py-2 rounded-lg">{order.token || order.tracking_token}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-cream-50 border-t border-cream-200 px-6 py-4 rounded-b-2xl flex justify-end gap-3">
          <button
            onClick={onClose}
            className="rounded-full px-6 py-2 bg-cream-200 text-ink-soft hover:bg-cream-300 transition-colors font-medium"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailModal;
